import {
  Button,
  ComposedModal,
  InlineLoading,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PasswordInput,
  TextInput,
} from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import { useRef, useState, type FormEvent } from 'react';
import './ManagerApproval.scss';

export interface ManagerApprovalProps {
  open: boolean;
  /** What is being approved, already formatted, e.g. "Void 1 × BBQ beefy, 219,000" (FR-AUTH-08). */
  summary: string;
  /** Resolves with a translated error message, or undefined once approved. */
  onSubmit(staffCode: string, password: string): Promise<string | undefined>;
  onCancel(): void;
}

const FORM_ID = 'pos-approval-form';

/**
 * A second person approves one restricted action with their staff code and password (§8.2, FR-AUTH-07). Each
 * approval covers exactly one action; there is no manager mode.
 */
export function ManagerApproval({ open, summary, onSubmit, onCancel }: ManagerApprovalProps) {
  const { t } = useTranslation();
  const [staffCode, setStaffCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  const close = () => {
    setStaffCode('');
    setPassword('');
    setError(undefined);
    onCancel();
  };

  const approve = async (event: FormEvent) => {
    event.preventDefault();
    if (!staffCode.trim() || !password || busy) return;
    setBusy(true);
    const message = await onSubmit(staffCode.trim(), password);
    setBusy(false);
    setPassword('');
    if (message) {
      setError(message);
      passwordRef.current?.focus();
    } else {
      setStaffCode('');
      setError(undefined);
    }
  };

  return (
    <ComposedModal
      open={open}
      onClose={close}
      size="sm"
      preventCloseOnClickOutside
      selectorPrimaryFocus="#pos-approver-code"
    >
      <ModalHeader title={t('approval.title')} label={summary} iconDescription={t('carbon.close')} />
      <ModalBody hasForm>
        <form id={FORM_ID} className="pos-approval-form" onSubmit={(e) => void approve(e)} noValidate>
          <p>{t('approval.hint')}</p>
          <TextInput
            id="pos-approver-code"
            size="lg"
            labelText={t('login.staffCode')}
            value={staffCode}
            autoComplete="off"
            onChange={(e) => setStaffCode(e.target.value)}
            onKeyDown={(e) => {
              // Enter moves on to the password, like the login; Enter there submits.
              if (e.key === 'Enter') {
                e.preventDefault();
                passwordRef.current?.focus();
              }
            }}
          />
          <PasswordInput
            id="pos-approver-password"
            ref={passwordRef}
            size="lg"
            labelText={t('login.password')}
            showPasswordLabel={t('carbon.showPassword')}
            hidePasswordLabel={t('carbon.hidePassword')}
            value={password}
            autoComplete="off"
            onChange={(e) => setPassword(e.target.value)}
          />
          {/* Lets Enter in a field submit; the visible Approve button lives in the footer. */}
          <button type="submit" hidden tabIndex={-1} aria-hidden="true" />
          {error && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              statusIconDescription={t('carbon.error')}
              title={error}
            />
          )}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={close}>
          {t('common.cancel')}
        </Button>
        {/* In the footer, outside the form element, but its submit button. */}
        <Button kind="primary" type="submit" form={FORM_ID} disabled={busy}>
          {busy ? <InlineLoading description={t('common.loading')} /> : t('approval.approve')}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}
